import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
  getPasskeys,
  removePasskey
} from '../services/passkeyService';

const PasskeySetup = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deviceName, setDeviceName] = useState('');

  useEffect(() => {
    checkSupport();
    loadPasskeys();
  }, []);

  const checkSupport = async () => {
    const supported = isPasskeySupported();
    setIsSupported(supported);
    
    if (supported) {
      const available = await isPlatformAuthenticatorAvailable();
      setIsAvailable(available);
    }
  };

  const loadPasskeys = async () => {
    try {
      const userPasskeys = await getPasskeys();
      setPasskeys(userPasskeys);
    } catch (error) {
      console.error('Error loading passkeys:', error);
    }
  };

  const handleRegisterPasskey = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }

    setLoading(true);
    try {
      await registerPasskey(deviceName.trim());
      toast.success('Passkey registered successfully!');
      setDeviceName('');
      loadPasskeys(); // Reload the list
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to register passkey');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePasskey = async (credentialId, deviceName) => {
    if (!window.confirm(`Are you sure you want to remove the passkey for "${deviceName}"?`)) {
      return;
    }

    try {
      await removePasskey(credentialId);
      toast.success('Passkey removed successfully');
      loadPasskeys(); // Reload the list
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove passkey');
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Passkeys Not Supported
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Firefox.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAvailable) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Platform Authenticator Not Available
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Your device doesn't have a platform authenticator (like Touch ID, Face ID, or Windows Hello) available.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Passkey Authentication</h3>
        <p className="text-sm text-gray-600">
          Set up passkeys for secure, passwordless authentication. Passkeys use your device's biometric authentication 
          (like fingerprint or face recognition) or PIN for secure login.
        </p>
      </div>

      {/* Register New Passkey */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-md font-medium text-gray-900 mb-3">Add New Passkey</h4>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Device name (e.g., MacBook Pro, iPhone)"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
          <button
            onClick={handleRegisterPasskey}
            disabled={loading || !deviceName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Adding...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Passkey
              </>
            )}
          </button>
        </div>
      </div>

      {/* Existing Passkeys */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-3">Your Passkeys</h4>
        {passkeys.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p>No passkeys registered yet</p>
            <p className="text-sm">Add a passkey above to enable passwordless login</p>
          </div>
        ) : (
          <div className="space-y-3">
            {passkeys.map((passkey) => (
              <div key={passkey.credentialId} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{passkey.deviceName}</p>
                    <p className="text-sm text-gray-500">
                      Created: {new Date(passkey.createdAt).toLocaleDateString()}
                      {passkey.lastUsed && (
                        <span className="ml-2">
                          • Last used: {new Date(passkey.lastUsed).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemovePasskey(passkey.credentialId, passkey.deviceName)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Remove passkey"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasskeySetup;